import DataService from "@/services/data.service";
import ClassUtility from "@/util/ClassUtility";
import i18n from '@/i18n'

import ITEM_ENHANCEMENTS from './resources/midnight/enhancements';
import EMBELLISHMENTS from './resources/midnight/embellishments';

const RATIO = { ENCHANTMENTS: 45, EMBELLISHMENTS: 20, SOCKETS: 35 };
const VALUE_ABBREVIATION = "midnight";
const MAX_QUALITY = 2;

class EquipmentHelper{
    resolveWowHeadData(item, locale) {
        let entries = [`domain=${locale}`];
        if (item.sockets) {
            entries.push(`gems=${item.sockets.map(x => x.id).join(":")}`);
        }

        if (item.enchantments) {
            entries.push(`ench=${item.enchantments[0].id}`);
        }

        if (item.bonus_list) {
            entries.push(`bonus=${item.bonus_list.join(":")}`);
        }

        entries.push(`ilvl=${item.level}`);
        return entries.join("&");
    }

    async parseSimcEquipItem(line, locale){
        let wowHeadData = [`domain=${locale}`];
        let values = line.split(",");

        let id = 0;
        let enchant = "";
        let sockets = [];
        let bonusIds = [];

        values.forEach(x => {
            if(x.length > 0 && x.includes("=")){
                let split = x.split("=");

                if(split[0] === "id"){
                    id = split[1];
                }

                if(split[0] === "enchant_id"){
                    enchant = split[1];
                    wowHeadData.push(`ench=${split[1]}`);
                }

                if(split[0] === "bonus_id"){
                    bonusIds = split[1].split("/");
                    wowHeadData.push(`bonus=${bonusIds.join(":")}`);
                }

                if(split[0] === "gem_id"){
                    sockets = split[1].split("/");
                    wowHeadData.push(`gems=${sockets.join(":")}`);
                }
            }
        });

        let media = await DataService.getItemMedia(parseInt(id));

        return {
            id: id,
            enchant: enchant,
            sockets: sockets,
            bonusIds: bonusIds,
            img_wowhead: media,
            data_wowhead: wowHeadData.join("&"),
            href_wowhead: `https://www.wowhead.com/item=${id}`,
            simcEntry: line
        };
    }

    resolveEquipVisualization(equipmentList, locale){
        equipmentList.forEach(equippedItem => {
            equippedItem.img_wowhead = equippedItem.media;
            equippedItem.data_wowhead = this.resolveWowHeadData(equippedItem, locale);
            equippedItem.href_wowhead = `https://www.wowhead.com/item=${equippedItem.id}`
        });
    }

    filterEquippedItems(items){
        if(items){
            return items.filter(x => x.type !== "TABARD" && x.type !== "SHIRT");
        } else {
            return [];
        }
    }

    gearIconStyle(item) {
        let itemColor;

        switch(item.quality){
            case "LEGENDARY":
                itemColor = "darkorange"
                break;
            case "EPIC":
                itemColor = "darkviolet"
                break;
            default:
                itemColor = "grey"
                break;
        }

        return {
            borderColor: itemColor
        }
    }

    getItemContext(item, locale) {
        return `domain=${locale}&bonus=${item.itemInfo.bonusIds.join(":")}`;
    }

    createScoring(character, scoreAllSockets, fullScoreLowCategory = false, enableDiagnostics = false) {
        let data = { detail: [] };
        const equipment = character["character_detail"].equipment || [];

        let characterInfo = {
            name: character.name,
            class: character.class,
            spec: character["character_detail"].activeSpec.name.toLowerCase(),
            role: ClassUtility.getRoleForSpec(character["character_detail"].activeSpec.name.toLowerCase()),
            main: ClassUtility.getMainStatForSpec(character["character_detail"].activeSpec.name.toLowerCase())
        }

        for (let key of Object.keys(ITEM_ENHANCEMENTS)) {
            let item = equipment.find(e => e.type===key);
            if(item && this.doCalculate(item)){
                data.detail.push(this.calculateScoreForItem(item, characterInfo, fullScoreLowCategory, scoreAllSockets));
            }
        }

        let embellishmentsFromBonusIds = equipment.map(e => e.bonus_list || []).flat().filter(e => EMBELLISHMENTS.bonusIds.find(f => e===f.id));
        let embellishmentsFromItemIds = equipment.map(e => e.id).filter(e => EMBELLISHMENTS.itemIds.find(f => e===f.id));
        let embellishments = embellishmentsFromBonusIds.length + embellishmentsFromItemIds.length;
        const cappedEmbellishments = Math.min(embellishments, 2);

        const embellishmentItems = equipment
            .map(item => {
                const matchedBonusIds = (item.bonus_list || []).filter(id => EMBELLISHMENTS.bonusIds.some(b => b.id === id));
                const matchedItemId = EMBELLISHMENTS.itemIds.some(i => i.id === item.id);
                if (!matchedBonusIds.length && !matchedItemId) {
                    return null;
                }

                return {
                    slot: item.type,
                    itemId: item.id,
                    matchedBonusIds,
                    matchedItemId,
                    rawContribution: matchedBonusIds.length + (matchedItemId ? 1 : 0)
                };
            })
            .filter(Boolean);

        let hints = data.detail.map(d => d.hint).flat();
        if (embellishments < 2) {
            hints.push(`${i18n.t('scoring.embellishment.missing', { count: embellishments })}`);
        }

        data.tooltip = hints.join("\n");
        data.sum = data.detail.length > 0
            ? ((data.detail.reduce((p, d) => p + d.score, 0) / data.detail.length) + (RATIO.EMBELLISHMENTS * cappedEmbellishments / 2))
            : 0;

        data.sum = data.sum.toFixed(1);

        const slotScores = data.detail.map(d => ({
            slot: i18n.t(`scoring.slots.${d.slot.toLowerCase()}`),
            score: parseFloat(d.score.toFixed(2)),
            log: `Item score - Slot: '${i18n.t(`scoring.slots.${d.slot.toLowerCase()}`)}' | Score: '${parseFloat(d.score.toFixed(2))}'`
        }));

        const overCapSlots = data.detail
            .filter(d => d.score > (100 - RATIO.EMBELLISHMENTS))
            .map(d => ({ slot: d.slot, score: parseFloat(d.score.toFixed(2)) }));

        const avgItemScore = data.detail.length > 0
            ? parseFloat((data.detail.reduce((p, d) => p + d.score, 0) / data.detail.length).toFixed(2))
            : 0;

        const totalScore = parseFloat(data.sum);
        if(enableDiagnostics){
            console.debug("Equipment score diagnostics:", {
                character: character.name,
                avgItemScore,
                totalScore,
                totalScoreLog: `Total equipment score: ${totalScore}`,
                embellishments: {
                    rawFromBonusIds: embellishmentsFromBonusIds.length,
                    rawFromItemIds: embellishmentsFromItemIds.length,
                    rawCombined: embellishments,
                    uniqueItemsMatched: embellishmentItems.length,
                    cappedUsedInScore: cappedEmbellishments,
                    details: embellishmentItems
                },
                slotScores,
                overCapSlots
            });
        }
        return data;
    }

    calculateScoreForItem = (item, characterInfo, fullScoreLowCategory, scoreAllSockets) => {
        if (!item) return {score: 0, hint: "", slot: "none"}
        let hints = [];
        let conditionMultiplier = 1;
        let type = item.type;
        let translatedType = i18n.t(`scoring.slots.${type.toLowerCase()}`);

        let ITEM_ENHANCEMENT;
        if(ITEM_ENHANCEMENTS.EXCEPTIONS[item.id]){
            ITEM_ENHANCEMENT = ITEM_ENHANCEMENTS.EXCEPTIONS[item.id];
        } else {
            ITEM_ENHANCEMENT = ITEM_ENHANCEMENTS[type];
            ITEM_ENHANCEMENT.SOCKET_LIST = ITEM_ENHANCEMENTS.SOCKET_LIST || [];
        }

        let enchantmentAvailable = ITEM_ENHANCEMENT.ENCHANTMENTS.length > 0;
        let enchantment = ITEM_ENHANCEMENT.ENCHANTMENTS.find(list => item.enchantments?.some(used => used.id === list.id));
        let enchantmentOnGear = item.enchantments?.length > 0;

        if (enchantment) {
            let translatedEnchantment = `${i18n.t(`scoring.enchantment.values.${VALUE_ABBREVIATION}.${enchantment.name}`)}`;
            if (enchantment.conditions && enchantment.conditions.length) {
                if (enchantment.conditions.find(c => c.type === "class") && enchantment.conditions.filter(c => c.type === "class").includes(characterInfo.class)) {
                    hints.push(i18n.t('scoring.enchantment.not_best.class', { slot: translatedType, current: translatedEnchantment, class: characterInfo.class }));
                    conditionMultiplier *= 0.8;
                }
                if (enchantment.conditions.find(c => c.type === "role") && enchantment.conditions.filter(c => c.type === "role").includes(characterInfo.role)) {
                    hints.push(i18n.t('scoring.enchantment.not_best.role', { slot: translatedType, current: translatedEnchantment, class: characterInfo.role }));
                    conditionMultiplier *= 0.8;
                }
                if (enchantment.conditions.find(c => c.type === "spec") && enchantment.conditions.filter(c => c.type === "spec").includes(characterInfo.spec)) {
                    hints.push(i18n.t('scoring.enchantment.not_best.spec', { slot: translatedType, current: translatedEnchantment, class: characterInfo.spec }));
                    conditionMultiplier *= 0.8;
                }
            }

            if (enchantment.quality < MAX_QUALITY) {
                hints.push(i18n.t('scoring.enchantment.quality', { slot: translatedType, current: translatedEnchantment, quality: enchantment.quality }));
            }
            else if ((enchantment.score * enchantment.scaling) < 100) {
                let alternatives = ITEM_ENHANCEMENT.ENCHANTMENTS.filter(e => (e.score * e.scaling) === 100).map(e => i18n.t(`scoring.enchantment.values.${VALUE_ABBREVIATION}.${e.name}`)).join(", ");
                hints.push(i18n.t('scoring.enchantment.not_best.general', { slot: translatedType, current: translatedEnchantment, alternatives: alternatives }));
            }

            if (enchantment.category === "low") {
                if (!fullScoreLowCategory) {
                    hints.push(i18n.t('scoring.enchantment.low_category', { slot: translatedType, current: translatedEnchantment }));
                } else {
                    hints.push(i18n.t('scoring.enchantment.low_category_info', { slot: translatedType, current: translatedEnchantment }));
                }
            }
        } else if (!enchantment && enchantmentAvailable) {
            if(type === "OFF_HAND" && item.inventory_type === "WEAPON" || type !== "OFF_HAND"){
                hints.push(i18n.t('scoring.enchantment.missing', { slot: translatedType }));
            }
        } else if(!enchantment && enchantmentOnGear){
            hints.push(i18n.t('scoring.enchantment.not_recognized', { slot: translatedType }));
        }

        let socketsSocketed = ITEM_ENHANCEMENT.SOCKET_LIST.filter(socket => item.sockets?.some(used => used.socketed && used.id === socket.id));
        let socketsOnGear = item.sockets?.filter(s => s.socketed).length > 0;
        if (socketsSocketed.length > 0) {
            socketsSocketed.forEach(socket => {
                let translatedSocket = `${i18n.t(`scoring.socket.values.${VALUE_ABBREVIATION}.${socket.name}`)}`;

                if (socket.conditions && socket.conditions.length) {
                    if (socket.conditions.find(c => c.type === "role") && socket.conditions.find(c => c.type === "role")?.value !== characterInfo.role) {
                        hints.push(i18n.t('scoring.socket.not_best.role', { slot: translatedType, current: translatedSocket, class: characterInfo.role }));
                        conditionMultiplier *= 0.8;
                    }
                }

                if (socket.quality < MAX_QUALITY) {
                    hints.push(i18n.t('scoring.socket.quality', { slot: translatedType, current: translatedSocket, quality: socket.quality }));
                }
                else if ((socket.score * socket.scaling) < 100) {
                    if(socket.description?.length > 0){
                        //show description in tooltip
                        hints.push(i18n.t('scoring.socket.not_best.general', { slot: translatedType, current: translatedSocket, description: i18n.t(`scoring.socket.description.${socket.description}`) }));

                    } else {
                        //show alternatives in tooltip
                        let alternatives = ITEM_ENHANCEMENT.SOCKET_LIST.filter(s => (s.score * s.scaling) === 100).filter(s => s.type === socket.type).map(s => i18n.t(`scoring.socket.values.${VALUE_ABBREVIATION}.${s.name}`)).join(", ");
                        hints.push(i18n.t('scoring.socket.not_best.alternatives', { slot: translatedType, current: translatedSocket, alternatives: alternatives }));
                    }
                }

                if (socket.category === "low") {
                    if (!fullScoreLowCategory) {
                        hints.push(i18n.t('scoring.socket.low_category', { slot: translatedType, current: translatedSocket }));
                    } else {
                        hints.push(i18n.t('scoring.socket.low_category_info', { slot: translatedType, current: translatedSocket }));
                    }
                }
            })
        } else if(socketsOnGear > 0){
            hints.push(i18n.t('scoring.socket.not_recognized', { slot: translatedType }));
        }


        let socketMaxQty = item.sockets?.length;
        let socketCurQty = item.sockets?.filter(s => s.socketed).length || 0;
        if (socketMaxQty > 0 && socketCurQty < socketMaxQty) {
            if (ITEM_ENHANCEMENT.SOCKET_CAN_BE_BOUGHT) {
                hints.push(`${i18n.t('scoring.socket.missing', { slot: translatedType, current: socketCurQty, max: socketMaxQty })}. ${i18n.t('scoring.socket.obtained.action_house')}.`);
            }
            else {
                hints.push(`${i18n.t('scoring.socket.missing', { slot: translatedType, current: socketCurQty, max: socketMaxQty })}. ${i18n.t('scoring.socket.obtained.great_vault')}.`);
            }
        }

        const scoreMax = (100 - RATIO.EMBELLISHMENTS); // Max-Punkte-Bereich: 80
        let score = 0;

        // Wenn scoreAllSockets=false und der Spieler keinen Socket gesteckt hat,
        // ignorieren wir den Socket-Slot für die Score-Berechnung → Enchantment allein = 100%
        const socketMaxQtyForScore = (!scoreAllSockets && socketCurQty === 0) ? 0 : socketMaxQty;

        // Bestimme die tatsächlich relevanten Enhancement-Arten für diesen Slot
        const hasEnchantSlot = enchantmentAvailable;
        const hasSocketSlot = socketMaxQtyForScore > 0;

        if (!hasEnchantSlot && !hasSocketSlot) {
            // Kein Enchant, kein Socket konfiguriert/relevant → dieser Slot ist immer perfekt
            score = scoreMax;
        } else if (hasEnchantSlot && !hasSocketSlot) {
            // Nur Enchant
            if (enchantment) {
                score = (enchantment.score * enchantment.scaling) * (scoreMax / 100) * this.getCategoryMultiplier(enchantment, fullScoreLowCategory) * conditionMultiplier;
            }
            // kein Enchant → score bleibt 0
        } else if (!hasEnchantSlot && hasSocketSlot) {
            // Nur Socket
            const socketsToCheck = item.sockets?.filter(s => s.socketed).map(x => ITEM_ENHANCEMENT.SOCKET_LIST.find(s => s.id === x.id)).filter(x => x) || [];
            if (socketsToCheck.length > 0) {
                const socketTotalScore = socketsToCheck.reduce((sum, socket) => {
                    return sum + (socket.score * socket.scaling) * this.getCategoryMultiplier(socket, fullScoreLowCategory);
                }, 0);
                score = (socketTotalScore / socketMaxQtyForScore) * (scoreMax / 100) * conditionMultiplier;
            }
            // kein Socket → score bleibt 0
        } else {
            // Enchant + Socket → Enchant 45%, Socket 35% von 100
            if (enchantment) {
                score += (enchantment.score * enchantment.scaling) * (RATIO.ENCHANTMENTS / 100) * this.getCategoryMultiplier(enchantment, fullScoreLowCategory) * conditionMultiplier;
            }

            const socketsToCheck = item.sockets?.filter(s => s.socketed).map(x => ITEM_ENHANCEMENT.SOCKET_LIST.find(s => s.id === x.id)).filter(x => x) || [];
            if (socketsToCheck.length > 0) {
                const socketTotalScore = socketsToCheck.reduce((sum, socket) => {
                    return sum + (socket.score * socket.scaling) * this.getCategoryMultiplier(socket, fullScoreLowCategory);
                }, 0);
                score += (socketTotalScore / socketMaxQtyForScore) * (RATIO.SOCKETS / 100) * conditionMultiplier;
            }
        }

        return { score, hint: hints, slot: type };
    };

    doCalculate(item) {
        if(item.type === "OFF_HAND"){
            return item.inventory_type === "WEAPON" || item.inventory_type === "TWOHWEAPON";
        }
        return true;
    }

    getCategoryMultiplier(entry, fullScoreLowCategory) {
        if (!fullScoreLowCategory && entry?.category === "low") {
            return 0.5;
        }
        return 1.0;
    }
}


export default new EquipmentHelper();